/**
 * <s-review-form> — Sends a product review to Judge.me, then submits the same form natively as a
 * Shopify contact form so the store inbox also receives it (with the shopper's phone number).
 *
 * Required child markup:
 *   details               — Wraps the form; opened automatically after a successful post.
 *   form                  — Shopify contact form with contact[Rating], contact[name], contact[email],
 *                           contact[phone], contact[Review title] and contact[body] fields.
 *   [data-status]         — Live region for the sending message.
 *   [data-review-posted]  — Present only after the contact form posted successfully.
 *
 * Data attributes:
 *   data-endpoint     — Judge.me "create review" URL.
 *   data-shop-domain  — The store's myshopify domain.
 *   data-product-id   — Shopify product ID the review belongs to.
 *   data-sending      — Translated "sending" message.
 */
class ReviewForm extends HTMLElement {
  #form = null;
  #status = null;
  #submitting = false;

  connectedCallback() {
    this.#form = this.querySelector('form');
    this.#status = this.querySelector('[data-status]');
    this.#form?.addEventListener('submit', this.#handleSubmit);

    const posted = this.querySelector('[data-review-posted]');
    if (posted) {
      const details = this.querySelector('details');
      if (details) details.open = true;
      posted.scrollIntoView({ block: 'center' });
    }
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#handleSubmit);
  }

  #handleSubmit = async (event) => {
    const form = this.#form;
    if (!form || this.#submitting) return;
    event.preventDefault();
    if (!form.reportValidity()) return;

    this.#submitting = true;
    const submit = form.querySelector('[type="submit"]');
    if (submit) submit.disabled = true;
    if (this.#status) this.#status.textContent = this.dataset.sending || '';

    const data = new FormData(form);
    const value = (name) => String(data.get(name) || '').trim();
    const payload = {
      shop_domain: this.dataset.shopDomain,
      platform: 'shopify',
      id: Number(this.dataset.productId),
      rating: Number(data.get('contact[Rating]')),
      name: value('contact[name]'),
      email: value('contact[email]'),
      title: value('contact[Review title]'),
      body: value('contact[body]'),
    };

    try {
      await fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch {
      // The contact form below still delivers the review to the store inbox.
    }

    form.submit();
  };
}

if (!customElements.get('s-review-form')) customElements.define('s-review-form', ReviewForm);
